'use client';

import qs from 'qs';

import { useCallback, useEffect, useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';
import { Box, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '@/app/utilities/helpers/axios';
import { encryptRequest, decryptResponse } from '@/app/utilities/helpers/security';

import DeviceManageLayout from '@/app/components/layouts/device-manage';
import { DeviceStatus } from '@/app/components/device-status/device-status';
import { PlaylistManage } from '@/app/components/playlist-manage/playlist-manage';
import { ActivationManage } from '@/app/components/activate';
import { Loading } from '@/app/components/loading';
import { isValidMacAddress } from '@/app/utilities/helpers/common';

export const ManagePlaylistView = ({ type, macAddress, deviceKey }) => {
  const [deviceData, setDeviceData] = useState({});
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const path = usePathname();
  const { enqueueSnackbar } = useSnackbar();

  const getDeviceData = useCallback(
    async token => {
      try {
        setLoading(true);

        const body = {
          data: encryptRequest({}),
        };

        const response = await axiosInstance.post(
          'J6fiNj1qwvmTAKnu5RTwo0RYjZAcJkOJ',
          qs.stringify(body),
          {
            headers: {
              'X-Token-Alias': token,
            },
            withCredentials: true,
          }
        );

        const responseData = decryptResponse(response.data);
        if (responseData.success) {
          setDeviceData(responseData.data);
          setPlaylists(responseData.playlists);
          setLoading(false);
        } else {
          enqueueSnackbar(responseData.message, { variant: 'warning' });
          router.push('/login');
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.setItem('redirectAfterLogin', path);
          router.push('/login');
          return;
        }
        enqueueSnackbar('Server Error', { variant: 'error' });
      }
    },
    [enqueueSnackbar, path, router]
  );

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (macAddress && deviceKey && isValidMacAddress(macAddress)) {
      localStorage.setItem('redirectAfterLogin', path);
      router.push(`/login?mac_address=${macAddress}&device_key=${deviceKey}`);
    } else if (token) {
      getDeviceData(token);
    } else {
      router.push('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <Loading />
  ) : (
    <DeviceManageLayout
      title={type === 'activate' ? 'Activate your device' : 'Manage your playlists'}
      description={
        type === 'activate'
          ? 'Your Gateway to Endless Entertainment'
          : 'Curate Your Perfect Entertainment Experience'
      }
    >
      <Box
        sx={{
          mx: 'auto',
          width: {
            md: '100%',
            lg: '1200px',
          },
          pl: 3,
          pr: 3,
        }}
      >
        <Stack gap={8}>
          <DeviceStatus device={deviceData} />
          {type === 'activate' ? (
            <ActivationManage
              device={deviceData}
              playlists={playlists}
              refresh={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  getDeviceData(token);
                } else {
                  router.push('/login');
                }
              }}
            />
          ) : (
            <PlaylistManage
              device={deviceData}
              tableData={playlists}
              refresh={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  getDeviceData(token);
                } else {
                  router.push('/login');
                }
              }}
            />
          )}
        </Stack>
      </Box>
    </DeviceManageLayout>
  );
};
